import type { Rail, Vec2 } from "../level";
import type { Checker } from "../mapcheck";
import { rad, type Local, type PieceKind, type Placed, type PrimKind } from "./types";

/**
 * An in-ground pool (#163): sunk into the floor, ±hu along and ±hw across,
 * its coping flush with the ground (h 0) and nothing sticking up. The only
 * primitive that goes below the floor: where it covers a point it takes the
 * floor's place (`surfaceAt` in level.ts), and anything standing on it
 * still wins where it's higher.
 *
 * Along u it's a shallow end (-u, `shallow` deep) and a deep end (+u,
 * `deep` deep), with a hip between them: the floor curves down from
 * `hipFrom` to `hipTo` (local u). Every wall is a full transition from the
 * floor to vert at the coping, as tall as the pool is deep there, so the
 * deep end's walls are tall and the shallow end's are mellow; the corners
 * are curved. A stair set (`steps`) can come down the shallow end's wall,
 * with a handrail beside it.
 *
 * An `arm` (#169) makes it an L: a second wing off the deep end, as deep,
 * reaching out past one side. The deck's corner where the wings meet is
 * sharp at the coping, and the wall below wraps round it in a cone (its
 * height goes by the distance to that corner), so the inside corner is
 * smooth to carve; the outside corners are curved like the others.
 */
export interface PoolPrim extends Placed {
  kind: "pool";
  hu: number;
  shallow: number;
  deep: number;
  hipFrom: number;
  hipTo: number;
  steps?: PoolSteps;
  arm?: PoolArm;
}

/** The L's second wing (#169): `hw` half-wide along u, flush with the deep end's wall, reaching `len` past the pool's `side`. */
export interface PoolArm {
  side: "w+" | "w-";
  hw: number;
  len: number;
}

/** Steps down the shallow end's wall (-u): `count` treads `run` deep, ±hw across, centred `w` across the pool. */
export interface PoolSteps {
  w: number;
  hw: number;
  count: number;
  run: number;
  /** A handrail beside the steps, on their +w or -w side (left out: none). */
  rail?: "w+" | "w-";
  /** How far back over the deck the handrail starts (m, #169; left out: 0.4). */
  lead?: number;
}

export interface PoolPiece {
  kind: "pool";
  x: number;
  z: number;
  rot?: number;
  hu: number;
  hw: number;
  shallow: number;
  deep: number;
  hipFrom: number;
  hipTo: number;
  steps?: PoolSteps;
  arm?: PoolArm;
}

/** How high a pool's handrail stands over the steps (m). */
export const POOL_RAIL_H = 0.9;

/** A pool's rectangle: everything `poolRect` reads, so the L's wings can be evaluated as rectangles too. */
type Rect = Pick<PoolPrim, "hu" | "hw" | "shallow" | "deep" | "hipFrom" | "hipTo" | "steps">;

/** How deep the floor is at local u, and how fast that changes along u. */
export function poolDepth(p: Rect, u: number): { d: number; dd: number } {
  if (u <= p.hipFrom) return { d: p.shallow, dd: 0 };
  if (u >= p.hipTo) return { d: p.deep, dd: 0 };
  const len = p.hipTo - p.hipFrom;
  const a = (Math.PI * (u - p.hipFrom)) / len;
  const k = (p.deep - p.shallow) / 2;
  return { d: p.shallow + k * (1 - Math.cos(a)), dd: (k * Math.PI * Math.sin(a)) / len };
}

/** The step (0 at the wall) covering local (u, w), or -1 off the steps. */
export function poolStepAt(p: Rect, u: number, w: number): number {
  const s = p.steps;
  if (!s || Math.abs(w - s.w) > s.hw) return -1;
  const k = Math.floor((u + p.hu) / s.run);
  return k >= 0 && k < s.count ? k : -1;
}

/** Tread `k`'s height (0 at the wall, below the deck). */
export function poolTreadH(p: Rect, k: number): number {
  return (-p.shallow * (k + 1)) / (p.steps!.count + 1);
}

/** A rectangular pool's height and slope at local (u, w). */
function poolRect(p: Rect, u: number, w: number): Local | null {
  if (Math.abs(u) > p.hu || Math.abs(w) > p.hw) return null;
  const k = poolStepAt(p, u, w);
  if (k >= 0) return { h: poolTreadH(p, k), du: 0, dw: 0 };
  const { d: depth, dd: slope } = poolDepth(p, u);
  const su = u < 0 ? -1 : 1;
  const sw = w < 0 ? -1 : 1;
  // How far past where each wall's transition starts: an end wall, or a side wall (which follows the floor's depth).
  const endR = u < 0 ? p.shallow : p.deep;
  const qe = u < 0 ? -u - (p.hu - p.shallow) : u - (p.hu - p.deep);
  const qs = Math.abs(w) - (p.hw - depth);
  let d: number;
  let r: number;
  let nu: number;
  let nw: number;
  let side = false;
  if (qe > 0 && qs > 0) {
    d = Math.hypot(qe, qs);
    r = endR;
    nu = (su * qe) / d;
    nw = (sw * qs) / d;
  } else if (qe >= qs) {
    d = qe;
    r = endR;
    nu = su;
    nw = 0;
  } else {
    d = qs;
    r = depth;
    nu = 0;
    nw = sw;
    side = true;
  }
  if (d <= 0) return { h: -depth, du: -slope, dw: 0 };
  if (d >= r) return { h: 0, du: 0, dw: 0 };
  const dd = Math.min(d, r - 1e-3);
  const root = Math.sqrt(r * r - dd * dd);
  const g = dd / root;
  // A side wall's radius and start both follow the floor's depth along u.
  const du = g * nu + (side ? (-slope * (r - dd)) / root : 0);
  const l: Local = { h: -root, du, dw: g * nw, quarter: { u: d, r, axis: { u: nu, w: nw } } };
  if (qe > 0 && qs > 0) l.quarter!.curved = true;
  return l;
}

/** The L's wings (#169), as rectangles in the frame mirrored so the arm is on +w: `wing` its own, `overlap` the deep end with the inside wall taken out. */
interface Wings {
  /** Where the arm starts along u: the deck's corner is (`u0`, hw). */
  u0: number;
  wing: Rect & { u: number; w: number };
  overlap: Rect & { u: number; w: number };
}

const WINGS = new WeakMap<PoolPrim, Wings>();

function wingsOf(p: PoolPrim, a: PoolArm): Wings {
  let out = WINGS.get(p);
  if (!out) {
    const u0 = p.hu - 2 * a.hw;
    const flat = (u: number, w: number, hu: number, hw: number) => ({ u, w, hu, hw, shallow: p.deep, deep: p.deep, hipFrom: 0, hipTo: 0 });
    const hw = p.hw + a.len / 2;
    // The overlap's own back wall stands a wall's depth behind the corner, so none of it reaches the deep end.
    const back = u0 - p.deep;
    out = { u0, wing: flat(p.hu - a.hw, a.len / 2, a.hw, hw), overlap: flat((back + p.hu) / 2, a.len / 2, (p.hu - back) / 2, hw) };
    WINGS.set(p, out);
  }
  return out;
}

/** `r`'s height at mirrored local (u, m), turned back into the pool's frame (`sw` the mirror). */
function inWing(r: Rect & { u: number; w: number }, u: number, m: number, sw: number): Local | null {
  const l = poolRect(r, u - r.u, m - r.w);
  if (!l || sw > 0) return l;
  l.dw = -l.dw;
  if (l.quarter?.axis) l.quarter.axis = { u: l.quarter.axis.u, w: -l.quarter.axis.w };
  return l;
}

/** An L pool's height at local (u, w): the main wing where only it covers, the arm's where only it does, and in the deep end they share the outside walls and the cone round the deck's corner. */
function poolL(p: PoolPrim, a: PoolArm, u: number, w: number): Local | null {
  const sw = a.side === "w+" ? 1 : -1;
  const m = sw * w;
  const { u0, wing, overlap } = wingsOf(p, a);
  if (Math.abs(u) > p.hu || m < -p.hw || m > p.hw + a.len) return null;
  if (u < u0) return m > p.hw ? null : poolRect(p, u, w);
  if (m > p.hw) return inWing(wing, u, m, sw);
  const out = inWing(overlap, u, m, sw);
  // The cone round the corner: as tall as the deep end, by the distance from the corner.
  const t = Math.hypot(u - u0, p.hw - m);
  const r = p.deep;
  if (t >= r) return out;
  if (t < 1e-9) return { h: 0, du: 0, dw: 0 };
  const d = Math.min(r - t, r - 1e-3);
  const root = Math.sqrt(r * r - d * d);
  // The nearer of the deck's edges is the one whose wall stands higher.
  if (out && out.h >= -root) return out;
  // Up the wall is toward the corner.
  const nu = (u0 - u) / t;
  const nw = (sw * (p.hw - m)) / t;
  const g = d / root;
  return { h: -root, du: g * nu, dw: g * nw, quarter: { u: d, r, axis: { u: nu, w: nw }, curved: true } };
}

export const POOL: PrimKind<PoolPrim> = {
  eval: (p, u, w) => (p.arm ? poolL(p, p.arm, u, w) : poolRect(p, u, w)),
  extent: (p) => [-p.hu, p.hu],
  across: (p) => (!p.arm ? [-p.hw, p.hw] : p.arm.side === "w+" ? [-p.hw, p.hw + p.arm.len] : [-p.hw - p.arm.len, p.hw]),
  shadow: 0,
  top: () => 0,
  plan: (p) => {
    const a = p.arm;
    if (!a) return [{ u0: -p.hu, u1: p.hu, fill: "ramp" }];
    const w = (a.side === "w+" ? 1 : -1) * (p.hw + a.len / 2);
    return [{ u0: -p.hu, u1: p.hu, fill: "ramp" }, { u0: p.hu - 2 * a.hw, u1: p.hu, w, hw: a.len / 2, fill: "ramp" }];
  },
};

/** The pool's coping in its local frame (#169): the straight runs (u0, w0, u1, w1) and each curved corner's middle, radius and which way it turns out (su, sw). */
export interface PoolCoping {
  straights: [number, number, number, number][];
  corners: { u: number; w: number; r: number; su: number; sw: number }[];
}

/** Coping round the rim: the shallow end's broken by the steps, an L's (#169) round both wings with a sharp corner where they meet. */
export function poolCoping(p: PoolPrim): PoolCoping {
  const [S, D] = [p.shallow, p.deep];
  const sw0 = p.hw - S;
  const st = p.steps;
  const ends: [number, number][] = st ? [[-sw0, st.w - st.hw], [st.w + st.hw, sw0]] : [[-sw0, sw0]];
  const shallowEnd = ends.filter(([w0, w1]) => w1 - w0 > 0.05).map(([w0, w1]): [number, number, number, number] => [-p.hu, w0, -p.hu, w1]);
  const corner = (u: number, w: number, su: number, sw: number, r = su < 0 ? S : D) => ({ u, w, r, su, sw });
  const a = p.arm;
  if (!a) {
    return {
      straights: [[p.hu, -(p.hw - D), p.hu, p.hw - D], [-(p.hu - S), p.hw, p.hu - D, p.hw], [-(p.hu - S), -p.hw, p.hu - D, -p.hw], ...shallowEnd],
      corners: [corner(p.hu - D, p.hw - D, 1, 1), corner(-(p.hu - S), sw0, -1, 1), corner(-(p.hu - S), -sw0, -1, -1), corner(p.hu - D, -(p.hw - D), 1, -1)],
    };
  }
  // Built with the arm on +w, then mirrored if it's on -w.
  const m = a.side === "w+" ? 1 : -1;
  const u0 = p.hu - 2 * a.hw;
  const far = p.hw + a.len;
  const runs: [number, number, number, number][] = [
    [p.hu, -(p.hw - D), p.hu, far - D],
    [-(p.hu - S), p.hw, u0, p.hw],
    [-(p.hu - S), -p.hw, p.hu - D, -p.hw],
    [u0, p.hw, u0, far - D],
    [u0 + D, far, p.hu - D, far],
  ];
  return {
    straights: [...runs.map(([u0, w0, u1, w1]): [number, number, number, number] => [u0, m * w0, u1, m * w1]), ...shallowEnd],
    corners: [
      corner(p.hu - D, m * (far - D), 1, m),
      corner(u0 + D, m * (far - D), -1, m, D),
      corner(-(p.hu - S), sw0, -1, 1),
      corner(-(p.hu - S), -sw0, -1, -1),
      corner(p.hu - D, -m * (p.hw - D), 1, -m),
    ],
  };
}

/** The pool's rails: its coping (`poolCoping`), and the steps' handrail. */
export function poolRails(p: PoolPrim): Rail[] {
  const c = Math.cos(p.rot);
  const s = Math.sin(p.rot);
  const at = (u: number, w: number): Vec2 => ({ x: p.x + u * c - w * s, z: p.z + u * s + w * c });
  const out: Rail[] = poolCoping(p).straights.map(([u0, w0, u1, w1]) => ({ a: at(u0, w0), b: at(u1, w1), y: 0, kind: "coping" }));
  const st = p.steps;
  if (st?.rail) {
    // Over the deck behind the steps (`lead`, #169), down past the last tread to the floor.
    const w = st.w + (st.rail === "w+" ? 1 : -1) * (st.hw + 0.15);
    const end = -p.hu + st.count * st.run + 0.4;
    out.push({ a: at(-p.hu - (st.lead ?? 0.4), w), b: at(end, w), y: POOL_RAIL_H, yb: POOL_RAIL_H - p.shallow, kind: "rail" });
  }
  return out;
}

export const POOL_PIECE: PieceKind<PoolPiece> = {
  fields: ["x", "z", "rot", "hu", "hw", "shallow", "deep", "hipFrom", "hipTo", "steps", "arm"],
  placed: true,
  check(c, o, path, bounds) {
    const hu = c.num(o, "hu", path, { min: 2, max: 2 * bounds });
    const hw = typeof o.hw === "number" ? o.hw : NaN;
    const shallow = c.num(o, "shallow", path, { min: 0.6, max: 3 });
    const deep = c.num(o, "deep", path, { min: 0.6, max: 5 });
    const hipFrom = c.num(o, "hipFrom", path, {});
    const hipTo = c.num(o, "hipTo", path, {});
    if (![hu, hw, shallow, deep, hipFrom, hipTo].every(Number.isFinite)) return;
    if (deep < shallow) c.fail(`${path}.deep`, "must be at least `shallow`");
    if (hw <= deep) c.fail(`${path}.hw`, "must be more than `deep`, to leave the deep end a floor");
    if (hipFrom < -(hu - shallow)) c.fail(`${path}.hipFrom`, `must be at least ${-(hu - shallow)}, past the shallow end's wall`);
    if (hipTo > hu - deep) c.fail(`${path}.hipTo`, `must be at most ${hu - deep}, short of the deep end's wall`);
    if (hipTo <= hipFrom) c.fail(`${path}.hipTo`, "must be past `hipFrom`");
    if (o.arm !== undefined) checkArm(c, o.arm, `${path}.arm`, { hu, deep, hipTo });
    if (o.steps === undefined) return;
    const st = c.obj(o.steps, `${path}.steps`);
    if (!st) return;
    const sp = `${path}.steps`;
    c.only(st, ["w", "hw", "count", "run", "rail", "lead"], sp);
    if (st.lead !== undefined) c.num(st, "lead", sp, { min: 0.4, max: 4 });
    const w = c.num(st, "w", sp, {});
    const shw = c.num(st, "hw", sp, { min: 0.4 });
    const count = c.num(st, "count", sp, { min: 1, max: 8 });
    const run = c.num(st, "run", sp, { min: 0.3, max: 1.5 });
    if (st.rail !== undefined && st.rail !== "w+" && st.rail !== "w-") c.fail(`${sp}.rail`, 'expected "w+" or "w-"');
    if (![w, shw, count, run].every(Number.isFinite)) return;
    if (!Number.isInteger(count)) c.fail(`${sp}.count`, "expected a whole number");
    if (Math.abs(w) + shw > hw - shallow) c.fail(`${sp}.w`, "the steps must fit the shallow end's straight wall");
    if (count * run < shallow) c.fail(`${sp}.run`, "the steps must reach past the wall's transition (count * run at least `shallow`)");
    if (-hu + count * run > hipFrom) c.fail(`${sp}.count`, "the steps must end before the hip");
  },
  compile(p, out) {
    const prim: PoolPrim = {
      kind: "pool",
      x: p.x,
      z: p.z,
      rot: rad(p.rot),
      hw: p.hw,
      hu: p.hu,
      shallow: p.shallow,
      deep: p.deep,
      hipFrom: p.hipFrom,
      hipTo: p.hipTo,
      ...(p.steps ? { steps: { ...p.steps } } : {}),
      ...(p.arm ? { arm: { ...p.arm } } : {}),
    };
    out.prims.push(prim);
    out.rails.push(...poolRails(prim));
  },
};

/** Checks an L's arm (#169): wide and long enough for its walls, and all of it at the deep end's depth. */
function checkArm(c: Checker, v: unknown, path: string, p: { hu: number; deep: number; hipTo: number }): void {
  const a = c.obj(v, path);
  if (!a) return;
  c.only(a, ["side", "hw", "len"], path);
  if (a.side !== "w+" && a.side !== "w-") c.fail(`${path}.side`, 'expected "w+" or "w-"');
  const hw = c.num(a, "hw", path, { min: p.deep, max: p.hu });
  const len = c.num(a, "len", path, { min: p.deep, max: 40 });
  if (![hw, len].every(Number.isFinite)) return;
  if (p.hipTo > p.hu - 2 * hw) c.fail(`${path}.hw`, `the arm must start past the hip (hipTo at most ${p.hu - 2 * hw})`);
}
